import * as THREE from 'three';
import { CSG } from 'three-csg-ts';
import { createPlaneFromInequality, createCuttingPlaneFromCut, createHalfSpaceMesh } from './geometry';
import { CanonicalCut } from '../core/cuts';
import { logger } from '@/app/logger'

// The result of a cutting operation.
export interface CuttingResult {
  remainingGeometry: THREE.BufferGeometry;
  cutAwayGeometry: THREE.BufferGeometry;
  volume: number;
  success: boolean;
  error?: string;
}

/**
 * Calculates the volume of a mesh using the accurate tetrahedral summation method.
 * This is much more accurate than bounding box for non-convex shapes.
 * @param geometry The buffer geometry of the mesh.
 * @returns The calculated volume.
 */
export function calculateMeshVolume(geometry: THREE.BufferGeometry): number {
  try {
    if (!geometry || !geometry.isBufferGeometry || !geometry.attributes.position) {
      logger.warn('Invalid geometry for volume calculation:', geometry);
    return 0;
  }

  const positions = geometry.attributes.position;
  const indices = geometry.index;
    
    if (!positions || positions.count === 0) {
      logger.warn('Geometry has no position data');
      return 0;
    }
    
  let volume = 0.0;
  const p1 = new THREE.Vector3();
  const p2 = new THREE.Vector3();
  const p3 = new THREE.Vector3();

    if (indices && indices.count > 0) {
    for (let i = 0; i < indices.count; i += 3) {
        try {
      p1.fromBufferAttribute(positions, indices.getX(i));
      p2.fromBufferAttribute(positions, indices.getX(i + 1));
      p3.fromBufferAttribute(positions, indices.getX(i + 2));
          
          // Check for invalid vertices
          if (!isFinite(p1.x) || !isFinite(p1.y) || !isFinite(p1.z) ||
              !isFinite(p2.x) || !isFinite(p2.y) || !isFinite(p2.z) ||
              !isFinite(p3.x) || !isFinite(p3.y) || !isFinite(p3.z)) {
            logger.warn('Invalid vertices found in geometry at triangle', i/3);
            continue;
          }
          
      volume += p1.dot(p2.clone().cross(p3));
        } catch (error) {
          logger.warn('Error processing triangle', i/3, ':', error as any);
          continue;
        }
    }
    } else if (positions.count >= 3) {
    for (let i = 0; i < positions.count; i += 3) {
        try {
      p1.fromBufferAttribute(positions, i);
      p2.fromBufferAttribute(positions, i + 1);
      p3.fromBufferAttribute(positions, i + 2);
          
          // Check for invalid vertices
          if (!isFinite(p1.x) || !isFinite(p1.y) || !isFinite(p1.z) ||
              !isFinite(p2.x) || !isFinite(p2.y) || !isFinite(p2.z) ||
              !isFinite(p3.x) || !isFinite(p3.y) || !isFinite(p3.z)) {
            logger.warn('Invalid vertices found in geometry at triangle', i/3);
            continue;
          }
          
      volume += p1.dot(p2.clone().cross(p3));
        } catch (error) {
          logger.warn('Error processing triangle', i/3, ':', error);
          continue;
        }
      }
    } else {
      logger.warn('Not enough vertices for volume calculation');
      return 0;
    }

    const finalVolume = Math.abs(volume / 6.0);
    
    if (!isFinite(finalVolume)) {
      logger.warn('Volume calculation resulted in non-finite value:', finalVolume);
      return 0;
    }
    
    logger.log('Calculated mesh volume:', finalVolume);
    return finalVolume;
    
  } catch (error) {
    logger.error('Error in calculateMeshVolume:', error as any);
    return 0;
  }
}

/**
 * Compute the CSG union of a list of convex meshes.
 * Returns a single mesh that represents the geometric union, or null if nothing valid.
 */
export function unionMeshes(meshes: THREE.Mesh[]): THREE.Mesh | null {
  if (!meshes || meshes.length === 0) return null;

  const valid: THREE.Mesh[] = [];
  for (const m of meshes) {
    const geom: any = m.geometry as any;
    const pos = geom?.attributes?.position;
    if (!geom || !pos || typeof pos.count !== 'number' || pos.count === 0) continue;
    valid.push(m);
  }

  if (valid.length === 0) return null;
  if (valid.length === 1) {
    const single = valid[0];
    const sourceMaterial = Array.isArray(single.material) ? single.material[0] : single.material;
    const material = sourceMaterial.clone();
    const clone = new THREE.Mesh(single.geometry.clone(), material);
    clone.castShadow = true;
    clone.receiveShadow = true;
    return clone;
  }

  const first = valid[0];
  first.updateMatrix();
  let csg = CSG.fromMesh(first);

  for (let i = 1; i < valid.length; i++) {
    const m = valid[i];
    m.updateMatrix();
    const csgB = CSG.fromMesh(m);
    csg = csg.union(csgB);
  }

  const sourceBaseMaterial = Array.isArray(first.material) ? first.material[0] : first.material;
  const baseMaterial = sourceBaseMaterial.clone();
  const resultMesh = CSG.toMesh(csg, first.matrix, baseMaterial);
  resultMesh.geometry.computeVertexNormals();
  resultMesh.castShadow = true;
  resultMesh.receiveShadow = true;

  return resultMesh;
}

/**
 * Performs the core CSG cut operation.
 * @returns A result object with the new geometries.
 */
function performCSGCut(targetMesh: THREE.Mesh, cut: CanonicalCut): Omit<CuttingResult, 'volume'> {
  const alpha3D = cut.alpha.length === 2 ? [...cut.alpha, 0] : cut.alpha.slice(0, 3);
  const plane = createPlaneFromInequality(alpha3D, cut.beta);
  
  // The cutting tool MUST use the same material as the target to prevent material mixing.
  // Handle both single material and material array cases
  const material = Array.isArray(targetMesh.material) ? targetMesh.material[0] : targetMesh.material;
  const cuttingTool = createHalfSpaceMesh(plane, material, false);
  
  targetMesh.updateMatrix();
  cuttingTool.updateMatrix();
  
  const cutAwayMesh = CSG.intersect(targetMesh, cuttingTool);
  const remainingMesh = CSG.subtract(targetMesh, cutAwayMesh);

  remainingMesh.geometry.computeVertexNormals();

  return {
    remainingGeometry: remainingMesh.geometry,
    cutAwayGeometry: cutAwayMesh.geometry,
    success: true
  };
}

/**
 * Manages the cutting process, including animations and state updates.
 */
export class PolyhedronCutter {
  private originalMesh: THREE.Mesh;
  private currentMesh: THREE.Mesh;
  private effects: CuttingEffects;

  constructor(originalMesh: THREE.Mesh, scene: THREE.Scene) {
    this.originalMesh = originalMesh;
    this.currentMesh = originalMesh.clone();
    this.effects = new CuttingEffects(scene);
  }

  /**
   * Applies a cut and returns a promise with the result, which is what main.ts expects.
   */
  applyCut(cut: CanonicalCut, animated: boolean = true): Promise<CuttingResult> {
    return new Promise((resolve) => {
      const executeCut = () => {
        try {
          const result = performCSGCut(this.currentMesh, cut);
          
          if (result.success) {
            // Create a new, clean mesh with the correct material.
            // Handle both single material and material array cases
            const material = Array.isArray(this.originalMesh.material) ? this.originalMesh.material[0] : this.originalMesh.material;
            const newMesh = new THREE.Mesh(result.remainingGeometry, material);
            newMesh.castShadow = true;
            newMesh.receiveShadow = true;

            // Dispose of the old geometry and update the internal state.
            this.currentMesh.geometry.dispose();
            this.currentMesh = newMesh;

            if (animated) {
              this.effects.createCutAwayExplosion(result.cutAwayGeometry);
            }

            resolve({
              ...result,
              volume: calculateMeshVolume(this.currentMesh.geometry),
            });
          } else {
            resolve({ ...result, volume: calculateMeshVolume(this.currentMesh.geometry), success: false, error: 'CSG failed' });
          }
        } catch (error) {
          logger.error("Error during cut execution:", error);
          resolve({ 
              remainingGeometry: this.currentMesh.geometry, 
              cutAwayGeometry: new THREE.BufferGeometry(),
              volume: calculateMeshVolume(this.currentMesh.geometry), 
              success: false, 
              error: error instanceof Error ? error.message : 'Unknown error' 
            });
        }
      };

      if (animated) {
        const alpha3D = cut.alpha.length === 2 ? [...cut.alpha, 0] : cut.alpha;
        this.effects.animateCuttingPlane(alpha3D, cut.beta, 1000, executeCut);
      } else {
        executeCut();
      }
    });
  }

  getCurrentMesh(): THREE.Mesh {
    return this.currentMesh;
  }

  getVolumeReduction(): number {
    const originalVolume = calculateMeshVolume(this.originalMesh.geometry);
    const currentVolume = calculateMeshVolume(this.currentMesh.geometry);
    if (originalVolume === 0) return 0;
    return ((originalVolume - currentVolume) / originalVolume) * 100;
  }

  reset(): void {
    this.currentMesh.geometry.dispose();
    this.currentMesh = this.originalMesh.clone();
  }
}

/**
 * Handles visual effects for the cutting process.
 */
class CuttingEffects {
    private scene: THREE.Scene;
    constructor(scene: THREE.Scene) { this.scene = scene; }

    animateCuttingPlane(alpha: number[], beta: number, duration: number, onComplete?: () => void): void {
        try {
            const result = createCuttingPlaneFromCut({ alpha, beta, efficacy: 0, sourceRow: 0, cgfType: 'gmi' }, 10);
            
            // Add defensive checks
            if (!result || !result.visualization || !result.wireframe) {
                logger.error('Failed to create cutting plane visualization:', { alpha, beta });
                if (onComplete) onComplete();
                return;
            }
            
            const { visualization, wireframe } = result;
            
            // Check if visualization has position property
            if (!visualization.position) {
                logger.error('Visualization object missing position property:', visualization);
                if (onComplete) onComplete();
                return;
            }
            
        const finalPosition = visualization.position.clone();
            
            // Check if visualization has userData.normal, provide fallback
            let normal: THREE.Vector3;
            if (visualization.userData && visualization.userData.normal) {
                normal = visualization.userData.normal.clone();
            } else {
                // Create fallback normal from alpha coefficients
                normal = new THREE.Vector3(alpha[0] || 0, alpha[1] || 0, alpha[2] || 0).normalize();
                logger.warn('Using fallback normal for cutting plane animation');
            }
            
            const startPosition = finalPosition.clone().add(normal.multiplyScalar(-20));
        visualization.position.copy(startPosition);
        wireframe.position.copy(startPosition);
        this.scene.add(visualization, wireframe);

        const startTime = Date.now();
        const animate = () => {
            const progress = Math.min((Date.now() - startTime) / duration, 1);
            const easedProgress = 1 - Math.pow(1 - progress, 3); // easeOutCubic
            visualization.position.lerpVectors(startPosition, finalPosition, easedProgress);
            wireframe.position.copy(visualization.position);

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                this.scene.remove(visualization, wireframe);
                if (onComplete) onComplete();
            }
        };
        animate();
        } catch (error) {
            logger.error('Error in animateCuttingPlane:', error);
            if (onComplete) onComplete();
        }
    }
    
    createCutAwayExplosion(cutAwayGeometry: THREE.BufferGeometry): void {
        const particleCount = Math.min(50, Math.floor(cutAwayGeometry.attributes.position.count / 3));
        if (particleCount === 0) return;
        // Explosion effect logic can be added here if needed.
    }
}
